/**
 * Single source of truth for the SAML protocol mappers Prism can create.
 *
 * Both the Create SAML Mapper dialog and the mapper detail panel read from
 * MAPPER_TYPES, so the labels an admin picks at creation are the labels they see
 * afterwards.
 *
 * `fields` lists the config keys the dialog takes input for, in dialog order.
 * Keys Prism hardcodes are deliberately absent: `attribute.nameformat` is always
 * 'Basic', and `single` is always 'false' on a role-list mapper. Surfacing a value
 * the admin cannot influence is noise.
 */
export const MAPPER_TYPES = [
  {
    value: 'user-attribute',
    label: 'User Attribute',
    protocolMapper: 'saml-user-property-mapper',
    helperText: 'Map user properties to SAML attributes',
    fields: [
      { key: 'attribute.name', label: 'SAML attribute name' },
      { key: 'user.attribute', label: 'User attribute' },
      { key: 'friendly.name', label: 'Friendly name' },
    ],
  },
  {
    value: 'role-list',
    label: 'Role List',
    protocolMapper: 'saml-role-list-mapper',
    helperText: 'Map user roles to SAML assertion',
    fields: [
      { key: 'attribute.name', label: 'SAML attribute name' },
      { key: 'friendly.name', label: 'Friendly name' },
    ],
  },
  {
    value: 'group-list',
    label: 'Group List',
    protocolMapper: 'saml-group-membership-mapper',
    helperText: 'Map user group memberships to SAML assertion',
    fields: [
      { key: 'attribute.name', label: 'SAML attribute name' },
      { key: 'friendly.name', label: 'Friendly name' },
      { key: 'full.path', label: 'Full group path' },
      { key: 'single', label: 'Single Group Attribute' },
    ],
  },
];

const BY_MAPPER_TYPE = Object.fromEntries(MAPPER_TYPES.map((t) => [t.value, t]));
const BY_PROTOCOL_MAPPER = Object.fromEntries(MAPPER_TYPES.map((t) => [t.protocolMapper, t]));

// Keycloak stores every config value as a string.
const BOOLEAN_VALUES = { true: true, false: false };

/** `some.custom.key` -> `Some custom key` */
const humanizeKey = (key) => {
  const words = key.replace(/[._-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Booleans arrive as the strings 'true'/'false'; everything else stays text. */
const toRow = (label, rawValue) =>
  rawValue in BOOLEAN_VALUES
    ? { label, value: BOOLEAN_VALUES[rawValue], kind: 'boolean' }
    : { label, value: rawValue, kind: 'text' };

/** The Keycloak `protocolMapper` a dialog mapper-type value maps to. */
export const getProtocolMapper = (mapperType) => BY_MAPPER_TYPE[mapperType]?.protocolMapper;

/** The helper text shown under the dialog's Mapper Type select. */
export const getMapperTypeHelperText = (mapperType) => BY_MAPPER_TYPE[mapperType]?.helperText || '';

/**
 * Display name for a mapper's `protocolMapper`, using the label the create dialog
 * shows. A type Prism cannot create returns the raw string, so it still identifies
 * itself rather than rendering blank.
 */
export const getMapperTypeLabel = (protocolMapper) => {
  if (!protocolMapper) return '';
  return BY_PROTOCOL_MAPPER[protocolMapper]?.label || protocolMapper;
};

/**
 * Flatten a mapper into ordered display rows, limited to what Prism lets an admin
 * configure: the mapper type and name, then the create dialog's fields for that type.
 *
 * @returns {Array<{label: string, value: *, kind: 'text'|'boolean'}>}
 */
export const getMapperDetailRows = (mapper) => {
  if (!mapper) return [];

  const rows = [];

  if (mapper.protocolMapper) {
    rows.push({ label: 'Mapper type', value: getMapperTypeLabel(mapper.protocolMapper), kind: 'text' });
  }
  if (mapper.name) rows.push({ label: 'Name', value: mapper.name, kind: 'text' });

  const config = mapper.config || {};
  const settableFields = BY_PROTOCOL_MAPPER[mapper.protocolMapper]?.fields;

  if (settableFields) {
    settableFields.forEach(({ key, label }) => {
      if (key in config) rows.push(toRow(label, config[key]));
    });
    return rows;
  }

  // A mapper type Prism cannot create — an imported client can carry one. There is no
  // dialog to mirror, so show the whole config rather than hide how the mapper behaves.
  Object.keys(config)
    .sort()
    .forEach((key) => rows.push(toRow(humanizeKey(key), config[key])));

  return rows;
};
